import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Circle,
  ChevronRight,
  X,
  Users,
  Shield,
  Download,
  Zap,
  Webhook,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const checklistItems = [
  { id: 'install_agent', label: 'Install agent on a device', icon: Download, link: '/agent', description: 'Protect your first device' },
  { id: 'invite_team', label: 'Invite team members', icon: Users, link: '/users', description: 'Add colleagues to your organization' },
  { id: 'configure_security', label: 'Configure security settings', icon: Shield, link: '/settings', description: 'Customize your protection' },
  { id: 'setup_automation', label: 'Set up automation rules', icon: Zap, link: '/automation', description: 'Automate threat response' },
  { id: 'add_webhook', label: 'Add a webhook', icon: Webhook, link: '/webhooks', description: 'Get real-time notifications' },
];

export default function OnboardingChecklist({ onDismiss }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [completedItems, setCompletedItems] = useState([]);

  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/onboarding/status`);
        setStatus(response.data);
        
        // If onboarding is complete and tour_completed, don't show
        if (response.data.status === 'active' && response.data.onboarding?.data?.tour_completed) {
          const storedDismissed = localStorage.getItem('onboarding_checklist_dismissed');
          if (storedDismissed) {
            setDismissed(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch onboarding status:', error);
      }
    };

    const checkCompletedItems = async () => {
      try {
        // Check devices
        const devicesRes = await axios.get(`${API}/api/devices`);
        if (devicesRes.data.length > 0) {
          setCompletedItems(prev => [...prev, 'install_agent']);
        }

        // Check users (more than 1 means invited) - API returns array directly
        const usersRes = await axios.get(`${API}/api/auth/users`);
        if (usersRes.data?.length > 1) {
          setCompletedItems(prev => [...prev, 'invite_team']);
        }

        // Check automation rules
        const rulesRes = await axios.get(`${API}/api/automation/rules`);
        if (rulesRes.data.length > 0) {
          setCompletedItems(prev => [...prev, 'setup_automation']);
        }

        // Check webhooks
        const webhooksRes = await axios.get(`${API}/api/webhooks`);
        if (webhooksRes.data.webhooks?.length > 0) {
          setCompletedItems(prev => [...prev, 'add_webhook']);
        }

        // Always mark security as done if they passed onboarding
        setCompletedItems(prev => [...prev, 'configure_security']);
      } catch (error) {
        console.error('Failed to check completed items:', error);
      }
    };

    fetchOnboardingStatus();
    checkCompletedItems();
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('onboarding_checklist_dismissed', 'true');
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  const handleItemClick = (item) => {
    navigate(item.link);
  };

  // Don't show if dismissed or no status
  if (dismissed || !status) return null;

  // Don't show for trial users who haven't started onboarding
  if (status.status !== 'onboarding' && status.status !== 'active') return null;

  const progress = (completedItems.length / checklistItems.length) * 100;
  const allCompleted = completedItems.length === checklistItems.length;

  if (allCompleted) {
    return (
      <Card className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/20 mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <div>
                <h3 className="text-white font-medium">Setup Complete!</h3>
                <p className="text-slate-400 text-sm">You&apos;ve completed all onboarding steps</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-slate-400">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-slate-800 mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-white">Getting Started</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-slate-500 hover:text-slate-300">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <Progress value={progress} className="h-2 flex-1" />
          <span className="text-sm text-slate-400">{completedItems.length}/{checklistItems.length}</span>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-2">
          {checklistItems.map((item) => {
            const Icon = item.icon;
            const isCompleted = completedItems.includes(item.id);
            
            return (
              <div
                key={item.id}
                onClick={() => !isCompleted && handleItemClick(item)}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  isCompleted
                    ? 'bg-emerald-500/10 cursor-default'
                    : 'bg-slate-800/50 hover:bg-slate-800 cursor-pointer'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-600 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isCompleted ? 'text-emerald-400' : 'text-white'}`}>
                    {item.label}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{item.description}</p>
                </div>
                {!isCompleted && (
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
